import { useCallback, useRef } from "react";

export type ResizeDirection = "n" | "s" | "e" | "w" | "nw" | "ne" | "sw" | "se";

type PanelSize = { width: number; height: number };
type PanelPosition = { x: number; y: number };

type UsePanelResizeParams = {
	panelSize: PanelSize;
	panelPosition: PanelPosition;
	setPanelSize: React.Dispatch<React.SetStateAction<PanelSize>>;
	setPanelPosition: React.Dispatch<React.SetStateAction<PanelPosition>>;
	minSize?: PanelSize;
	maxSize?: PanelSize;
};

export const usePanelResize = ({
	panelSize,
	panelPosition,
	setPanelSize,
	setPanelPosition,
	minSize = { width: 400, height: 400 },
	maxSize = { width: 1200, height: 900 },
}: UsePanelResizeParams) => {
	const resizeRef = useRef<{
		startX: number;
		startY: number;
		width: number;
		height: number;
		posX: number;
		posY: number;
		direction: ResizeDirection;
	} | null>(null);

	const createResizeHandlers = useCallback(
		(direction: ResizeDirection) => ({
			onPointerDown: (e: React.PointerEvent<HTMLDivElement>) => {
				e.stopPropagation();
				resizeRef.current = {
					startX: e.clientX,
					startY: e.clientY,
					width: panelSize.width,
					height: panelSize.height,
					posX: panelPosition.x,
					posY: panelPosition.y,
					direction,
				};
				e.currentTarget.setPointerCapture(e.pointerId);
			},
			onPointerMove: (e: React.PointerEvent<HTMLDivElement>) => {
				if (!resizeRef.current) return;
				const dx = e.clientX - resizeRef.current.startX;
				const dy = e.clientY - resizeRef.current.startY;
				const dir = resizeRef.current.direction;

				let newWidth = resizeRef.current.width;
				let newHeight = resizeRef.current.height;
				let newPosX = resizeRef.current.posX;
				let newPosY = resizeRef.current.posY;

				// パネルは right/bottom で配置されているため:
				// - 左方向(w)に広げる: width増加
				// - 右方向(e)に広げる: width増加 + posX増加（右端を動かす）
				// - 上方向(n)に広げる: height増加
				// - 下方向(s)に広げる: height増加 + posY増加（下端を動かす）

				if (dir.includes("w")) {
					newWidth = resizeRef.current.width - dx;
				}
				if (dir.includes("e")) {
					newWidth = resizeRef.current.width + dx;
					newPosX = resizeRef.current.posX - dx;
				}
				if (dir.includes("n")) {
					newHeight = resizeRef.current.height - dy;
				}
				if (dir.includes("s")) {
					newHeight = resizeRef.current.height + dy;
					newPosY = resizeRef.current.posY - dy;
				}

				newWidth = Math.min(maxSize.width, Math.max(minSize.width, newWidth));
				newHeight = Math.min(maxSize.height, Math.max(minSize.height, newHeight));
				newPosX = Math.max(0, newPosX);
				newPosY = Math.max(0, newPosY);

				setPanelSize({ width: newWidth, height: newHeight });
				setPanelPosition({ x: newPosX, y: newPosY });
			},
			onPointerUp: () => {
				resizeRef.current = null;
			},
		}),
		[
			panelSize,
			panelPosition,
			setPanelSize,
			setPanelPosition,
			minSize.width,
			minSize.height,
			maxSize.width,
			maxSize.height,
		],
	);

	return { createResizeHandlers };
};
